import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { errorMessage } from "../api";
import type { Item, StatusCategory } from "../api/types";
import { EmptyState, ErrorBox, Spinner } from "../components/bits";
import { Icon } from "../components/icons";
import { bucketOfDue, type DueBucket } from "../lib/buckets";
import { CATEGORY_LABELS, dueMoment, isClosed } from "../lib/format";
import { categoryColumns } from "../lib/kanban";
import { loadCollection, loadMyTasks, moveItem, toastError } from "../store/app";
import { byPosition, useAllCollections, useApp, useMyActorId } from "../store/hooks";
import { Board, type BoardColumnDef } from "./BoardView";
import { CalendarView } from "./CalendarView";
import { sortedStatuses, VIEW_ICONS, VIEW_NAMES } from "./CollectionPage";
import { CollectionChip, ItemRow } from "./ListView";
import { TimelineView } from "./TimelineView";

type Bucket = DueBucket;
const BUCKETS: { key: Bucket; title: string }[] = [
  { key: "overdue", title: "Overdue" },
  { key: "today", title: "Today" },
  { key: "upcoming", title: "Upcoming" },
  { key: "none", title: "No date" },
];

type MyView = "list" | "board" | "calendar" | "timeline";
const VIEWS: MyView[] = ["list", "board", "calendar", "timeline"];

export function MyTasksPage() {
  const [params, setParams] = useSearchParams();
  const myActorId = useMyActorId();
  const loaded = useApp((s) => s.myTasksLoaded);
  const items = useApp((s) => s.items);
  const details = useApp((s) => s.details);
  const collections = useAllCollections();
  const [error, setError] = useState<string | null>(null);
  const [includeDone, setIncludeDone] = useState(false);

  const view: MyView = VIEWS.find((v) => v === params.get("view")) ?? "list";

  const reload = () => {
    setError(null);
    loadMyTasks(includeDone).catch((e) => setError(errorMessage(e)));
  };
  useEffect(reload, [includeDone]);

  const mine = useMemo(
    () =>
      myActorId
        ? Object.values(items).filter(
            (i) =>
              i.assignees.includes(myActorId) &&
              (includeDone || !isClosed(i.status_category)) &&
              i.type !== "meeting",
          )
        : [],
    [items, myActorId, includeDone],
  );

  // Need statuses of each involved collection for the done checkbox.
  const collectionIds = useMemo(() => [...new Set(mine.map((i) => i.collection_id))], [mine]);
  useEffect(() => {
    for (const c of collectionIds) if (!details[c]) void loadCollection(c).catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collectionIds.join(",")]);
  const allStatuses = useMemo(() => collectionIds.flatMap((c) => sortedStatuses(details[c])), [collectionIds, details]);

  const grouped = useMemo(() => {
    const g: Record<Bucket, Item[]> = { overdue: [], today: [], upcoming: [], none: [] };
    for (const i of mine) g[bucketOfDue(i)].push(i);
    const byDue = (a: Item, b: Item) =>
      (dueMoment(a)?.getTime() ?? 0) - (dueMoment(b)?.getTime() ?? 0) || b.priority - a.priority;
    g.overdue.sort(byDue);
    g.today.sort(byDue);
    g.upcoming.sort(byDue);
    g.none.sort((a, b) => b.priority - a.priority || byPosition(a, b));
    return g;
  }, [mine]);

  const nameOf = useCallback((id: string) => collections.find((c) => c.id === id)?.name, [collections]);
  const chip = useCallback(
    (item: Item) => <CollectionChip id={item.collection_id} name={nameOf(item.collection_id) ?? "…"} />,
    [nameOf],
  );
  const board = useMemo(() => categoryColumns(mine), [mine]);
  const boardColumns = useMemo<BoardColumnDef[]>(
    () => board.keys.map((cat) => ({ key: cat, title: CATEGORY_LABELS[cat], category: cat })),
    [board.keys],
  );

  return (
    <div className="page">
      <header className="page-head">
        <h1>My tasks</h1>
        <div className="seg" role="tablist" aria-label="View">
          {VIEWS.map((v) => (
            <button
              key={v}
              type="button"
              role="tab"
              aria-selected={v === view}
              className={v === view ? "active" : ""}
              onClick={() => setParams({ view: v }, { replace: true })}
            >
              <Icon name={VIEW_ICONS[v]} size={14} />
              <span className="seg-label">{VIEW_NAMES[v]}</span>
            </button>
          ))}
        </div>
      </header>
      <div className="space-toolbar">
        <label className="check-row">
          <input type="checkbox" checked={includeDone} onChange={(e) => setIncludeDone(e.target.checked)} />
          <span>Include done</span>
        </label>
      </div>
      {error && <ErrorBox error={error} onRetry={reload} />}
      {!loaded && !error ? (
        <Spinner />
      ) : mine.length === 0 && view === "list" ? (
        <EmptyState title="Nothing assigned to you">
          <p className="muted">Tasks assigned to you across all collections show up here.</p>
        </EmptyState>
      ) : view === "board" ? (
        <Board
          columns={boardColumns}
          cards={board.columns}
          items={mine}
          cardExtra={chip}
          // Move-API neighbours must be siblings in the same collection.
          canBeSibling={(a, b) => a.collection_id === b.collection_id}
          onMoveCard={(id, cat, m) =>
            void moveItem(id, {
              status_category: cat as StatusCategory,
              after_id: m.afterId,
              before_id: m.beforeId,
              position: m.position,
            }).catch((err) => toastError(err, "Move failed"))
          }
        />
      ) : view === "calendar" ? (
        <CalendarView items={mine} />
      ) : view === "timeline" ? (
        <TimelineView items={mine} statuses={allStatuses} />
      ) : (
        BUCKETS.map(({ key, title }) =>
          grouped[key].length ? (
            <section key={key} className={`group bucket-${key}`}>
              <h2 className="group-head plain">
                {title} <span className="count">{grouped[key].length}</span>
              </h2>
              <ul className="rows">
                {grouped[key].map((it) => (
                  <ItemRow
                    key={it.id}
                    item={it}
                    statuses={sortedStatuses(details[it.collection_id])}
                    showCollection={nameOf(it.collection_id)}
                  />
                ))}
              </ul>
            </section>
          ) : null,
        )
      )}
    </div>
  );
}
